import React, { useMemo, useEffect, useState, FocusEvent, ChangeEvent, useCallback } from "react";
// Material Styling
import { FormControl, InputLabel, Grid, Button } from "@mui/material";
import { IconButton } from "../IconButton/IconButton";
import EditRounded from "@mui/icons-material/EditRounded";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import classNames from "classnames";
import { TextField } from "./TextField";
import { SelectField, ISelectFieldProps } from "./SelectField";
// Formik imports
import { FieldMetaProps } from "formik";
// RRRule Generator - for recurring events
import { RRule } from "rrule";
import { FieldError } from "./FieldError";

export interface IRRuleFieldProps {
  value?: any;
  label?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement> | any) => void;
  custom?: any;
  name?: string;
  className?: string;
  disabled?: boolean;
  selectionFieldProps?: Partial<ISelectFieldProps>;
  outputObject?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  saveButtonLabel?: string;
  onReset?: () => void;
}

interface IRRule {
  freq: IFreq["value"];
  count?: number;
  interval?: number;
  // wkst?: ,
  byweekday?: IWeekdayOption["value"] | IWeekdayOption["value"][]; // IWeekdayOption[],
  bymonth?: number | number[];
  bysetpos?: number | number[];
  bymonthday?: number | number[];
  byyearday?: number | number[];
  byweekno?: number | number[];
}
interface IOption {
  value: any;
  label: string;
}

type ValuesOf<T extends any[]> = T[any];

// type IFreqValue = IFreq['value'];
const freqOptions = [
  { value: 0, label: "Yearly" },
  { value: 1, label: "Monthly" },
  { value: 2, label: "Weekly" },
  { value: 3, label: "Daily" },
];
type IFreq = ValuesOf<typeof freqOptions>;

const weekdayOptions = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];
type IWeekdayOption = ValuesOf<typeof weekdayOptions>;

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];
type IMonthOption = ValuesOf<typeof monthOptions>;

// Convert a string of numbers to an array of integers.
// Split by space, comma or semi-colon
const stringToArray = (value: string): number[] => {
  if (!value) {
    return [];
  }
  const strArray = value.split(/[\s,; ]+/);
  const intArray = strArray.map((value: string) => {
    return !!value && typeof parseInt(value) === "number" ? parseInt(value) : null;
  });
  return intArray;
};

// Returns an array of values for the rrule constructor to use.
const getArrayOfValues = (options: IOption[]): any => {
  if (!!options && Array.isArray(options)) {
    const valuesArray = options.map((option: IOption) => option.value);
    return valuesArray;
  }
  return null;
};

const RRuleStyles = (theme: any): any => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "5px",
    flexWrap: "wrap",
    width: "calc(100% - 10px)",
    "& .input_control": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      "& label": {
        position: "relative",
        width: "80px",
        textAlign: "right",
        transform: "none",
        transition: "none",
        color: theme.custom.forms.pristineColor,
        fontSize: 12,
        lineHeight: "12px",
        margin: "5px 10px auto auto",
      },
      "& .edit_mode_button, & .view_mode_button": {
        padding: 0,
        margin: "2px 9px",
        border: `1px solid ${theme.custom.forms.pristineColor}`,
        borderRadius: "3px",
        width: "20px",
        height: "20px",
        "& svg": {
          color: theme.custom.forms.pristineColor,
          width: 14,
          height: 14,
        },
      },
      // Selection inputs
      "& .rrule_generator": {
        //height: "auto",
        maxHeight: 0,
        margin: "0px 0px 0px 38px",
        overflow: "hidden",
        transition: theme.transitions.create(["max-height", "margin"]),
        "&.show": {
          maxHeight: "100px",
          margin: "8px 0px 8px 38px",
          overflow: "visible",
        },
        "& .dayofmonth_selection .input_control div.input-base input": {
          maxWidth: 66,
        },
        "& .rules": { width: "auto", marginRight: 10 },

        "& .frequency_selection, & .monthly_selection, & .dayofmonth_selection, & .monthly_interval_selection, & .weekly_interval_selection, & .daily_interval_selection, & .weekday_selection":
          {
            // margin: "auto 10px auto auto",
            width: "auto",
            "& .input_control": {
              "& label": { width: "auto", margin: "0px 5px" },
              "& .react-select-container": { width: "auto" },
            },
          },
      },
      "& div": {
        margin: 0,
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        flexDirection: "row",
        "&.react-select__value-container": {
          display: "inline-grid",
        },
        "&.react-select__input-container": {
          "&:after": { display: "none" },
        },
        "&.freqSelect": {
          "& .react-select__value-container": {
            display: "block",
          },
        },
        "&.input-base": {
          borderRadius: "2px",
          border: "1px solid " + theme.custom.forms.inputBorder,
          color: theme.custom.forms.inputText,
          backgroundColor: theme.custom.forms.inputBack,
          transition: theme.transitions.create(["border-color", "color"]),
          "&:before": { display: "none" },
          "&:hover": {
            borderColor: theme.custom.forms.pristineColor,
          },
          "&[disabled]": {
            opacity: 0.5,
            borderStyle: "dotted",
            userSelect: "none",
          },
          "& input": {
            fontFamily: "Heebo, sans-serif",
            fontWeight: 300,
            margin: 0,
            maxWidth: 76,
            position: "relative",
            fontSize: 12,
            width: "auto",
            padding: "2px 8px",
            transition: theme.transitions.create(["color"]),
            boxSizing: "border-box",
          },
          "&.textarea-container": {
            // padding: "0px 8px",
            padding: 0,
            border: 0, // already applied to the input-base
            "&:before": { display: "none" },
            "& textarea": {
              fontSize: 12,
              fontWeight: 300,
              padding: "2px 0px",
            },
          },
        },
      },
    },
    "&.disabled .input_control .rrule_generator": {
      opacity: 0.5,
      userSelect: "none",
      pointerEvents: "none",
    },
    "&.error .input_control": {
      "& label": {
        color: theme.custom.forms.errorColor + "!important",
      },
      "& .edit_mode_button, & .view_mode_button": {
        border: `1px solid ${theme.custom.forms.errorColor}`,
        "& svg": { color: theme.custom.forms.errorColor },
      },
      "& div.input-base": {
        "&:after": {
          transform: "scaleX(1)",
          borderBottom: "2px solid " + theme.custom.forms.errorColor,
        },
      },
    },
    "&.dirty .input_control": {
      "& label": {
        color: theme.custom.forms.dirtyColor,
      },
      "& .edit_mode_button, & .view_mode_button": {
        border: `1px solid ${theme.custom.forms.dirtyColor}`,
        "& svg": { color: theme.custom.forms.dirtyColor },
      },
      "& div.input-base": {
        border: "1px solid " + theme.custom.forms.dirtyColor,
      },
    },
    "&.focused .input_control": {
      "& label": {
        color: theme.custom.forms.focusedColor,
      },
      "& .edit_mode_button, & .view_mode_button": {
        border: `1px solid ${theme.custom.forms.focusedColor}`,
        "& svg": { color: theme.custom.forms.focusedColor },
      },
      "& div.input-base": {
        border: "1px solid " + theme.custom.forms.pristineColor,
      },
    },
    "& .input_control .rrule_string": {
      pointerEvents: "none", // disable mouse events on this field.
      width: "calc(100% - 50px)",
      minWidth: 400,
      // margin: 5,
      fontWeight: 300,
      "& .input-base.MuiInputBase-multiline": {
        fontSize: 12,
        width: "100%",
        "& textarea": { textTransform: "capitalize" },
      },
    },
    // Error Messages
    "& .error-message": {
      maxWidth: 300,
      //alignSelf: 'flex-end',
      padding: "0px 5px",
      fontWeight: 300,
      color: theme.palette.error.main,
      //textAlign: 'right'
    },
  },
});

/**
 * RRule Generator - uses https://github.com/jakubroztocil/rrule
 */
export const RRuleField = (props: IRRuleFieldProps): JSX.Element => {
  const {
    label,
    name,
    value,
    onFocus,
    onBlur,
    onChange,
    className,
    disabled,
    selectionFieldProps,
    outputObject,
    meta,
    saveButtonLabel,
    onReset,
  } = props;

  const [rule, setRule] = useState<RRule>(null); // Store rule internally.
  const [editmode, setEditmode] = useState<boolean>(false);

  // These values mirror what's available in the rrule library.
  // Structure can be on the demo found here - https://jakubroztocil.github.io/rrule/
  const [freq, setFreq] = useState<IFreq>(freqOptions[0]);
  // const [count, setCount] = useState<number>(null);
  const [interval, setInterval] = useState<any>(1);
  // const [wkst, setWkst] = useState<IWeekdayOption[]>([]);
  const [byweekday, setByweekday] = useState<IWeekdayOption[]>([]);
  const [bymonth, setBymonth] = useState<IMonthOption[]>([]);
  // const [bysetpos, setBysetpos] = useState<number|number[]>([]);
  const [bymonthday, setBymonthday] = useState<number[]>([]);
  // const [byyearday, setByyearday] = useState<number|number[]>([]);
  // const [byweekno, setByweekno] = useState<number|number[]>([]);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  useEffect(() => {
    // If controlled input value is passed as a string or object, check for options and parse initial values.
    const rRuleValue = !!value && typeof value === "string" ? RRule.fromString(value) : value;
    setRule(rRuleValue); // This needs to be here so that it doesn't get stuck in a render loop when setting initial values.

    if (!!rRuleValue && rRuleValue.hasOwnProperty("options")) {
      const newRule = rRuleValue.options;
      // Frequency
      setFreq(freqOptions[newRule.freq]);
      // Interval Count
      setInterval(newRule.interval);
      // By Week Day
      const byweekday = newRule.byweekday
        ? weekdayOptions.filter((option: IWeekdayOption) => newRule.byweekday.includes(option.value))
        : [];
      setByweekday(byweekday);
      // By Month
      const bymonth = newRule.bymonth
        ? monthOptions.filter((option: IMonthOption) => newRule.bymonth.includes(option.value))
        : [];
      setBymonth(bymonth);
      // By Month Day
      setBymonthday(newRule.bymonthday);
    } else {
      setDefaultRule();
    }
  }, [value]);

  // Only let user save changes if their current rRule is valid and differs from current value.
  const enableSave = useMemo(() => {
    return rule?.isFullyConvertibleToText && value !== rule?.toString();
  }, [rule, value]);

  const handleSaveRRuleChanges = useCallback(() => {
    if (outputObject) {
      onChange(rule);
    } else {
      onChange(rule?.toString() ?? null);
    }
  }, [rule, onChange, outputObject]);

  const handleFocus = (e: React.FocusEvent<HTMLElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e as FocusEvent<HTMLInputElement>);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as FocusEvent<HTMLInputElement>);
      }
    }, 1);
  };

  // If any of the recurrence rules change, recreate the rule.
  useEffect(() => {
    let params: IRRule;
    let validParams = false;
    let valid1: boolean, valid2: boolean;
    switch (freq.value) {
      case RRule.YEARLY:
        params = {
          freq: freq.value,
          bymonth: getArrayOfValues(bymonth),
          bymonthday: bymonthday,
        };
        valid1 = Array.isArray(params.bymonth) ? params.bymonth.length > 0 : params.bymonth > 0;
        valid2 = Array.isArray(params.bymonthday) ? params.bymonthday.length > 0 : params.bymonthday > 0;
        validParams = valid1 && valid2 ? true : false;
        break;
      case RRule.MONTHLY:
        params = {
          freq: freq.value,
          interval: parseInt(interval),
          bymonthday: bymonthday,
        };
        valid1 = interval > 0;
        valid2 = Array.isArray(params.bymonthday) ? params.bymonthday.length > 0 : params.bymonthday > 0;
        validParams = valid1 && valid2 ? true : false;
        break;
      case RRule.WEEKLY:
        params = {
          freq: freq.value,
          interval: parseInt(interval),
          byweekday: getArrayOfValues(byweekday),
        };
        valid1 = interval > 0;
        valid2 = byweekday.length > 0;
        validParams = valid1 && valid2 ? true : false;
        break;
      case RRule.DAILY:
        params = { freq: freq.value, interval: parseInt(interval) };
        validParams = interval > 0 ? true : false;
        break;
      default:
        break;
    }

    if (validParams) {
      const newRule = new RRule(params);
      setRule(newRule);
    } else {
      setRule(null);
    }
  }, [freq, interval, byweekday, bymonth, bymonthday]);

  // If there is no valid rule input, set default value.
  const setDefaultRule = (): void => {
    setFreq(freqOptions[0]);
    setInterval(1);
    setByweekday([]);
    setBymonth([]);
    setBymonthday([]);
  };

  // Return the recurrence rule formatted in an english language string.
  const renderRule = (): JSX.Element => {
    // const rulePrefix = `${freq.label} — `;
    const ruleString: string = !!rule && typeof rule.toText == "function" ? rule.toText() : "";
    return (
      <Grid container direction="row">
        {editmode && (
          <IconButton
            id="rrule_view_mode_btn"
            className="view_mode_button"
            onClick={() => setEditmode(false)}
            size="large"
          >
            <ArrowDropUp />
          </IconButton>
        )}
        {!editmode && (
          <IconButton
            id="rrule_edit_mode_btn"
            className="edit_mode_button"
            onClick={() => setEditmode(true)}
            size="large"
          >
            <EditRounded />
          </IconButton>
        )}
        <TextField
          className="rrule_string"
          name="ruleString"
          value={ruleString}
          classes={{ root: classNames("input-base", "textarea-container") }}
          placeholder="create recurrence rule..."
          multiline={true}
          maxRows="4"
        />
      </Grid>
    );
  };

  // RENDER RULE FREQUENCY
  const renderFrequency = (): JSX.Element => {
    switch (freq.value) {
      case RRule.YEARLY:
        return (
          <Grid item className={classNames("rules", "yearly")}>
            {/* On specific day of specific month */}
            <SelectField
              label="every"
              className="monthly_selection freqSelect"
              name="rrule_bymonth"
              options={monthOptions}
              onChange={(value: any) => setBymonth(value)}
              value={bymonth}
              isMulti={true}
              isOptionSelected={(option: any, selection: any) => selection.includes(option)}
              hideSelectedOptions={false}
              openMenuOnFocus={false}
              menuPosition="fixed"
              isClearable={false}
              {...selectionFieldProps}
            />
            <TextField
              label="on the"
              className="dayofmonth_selection"
              name="bymonthday"
              value={bymonthday ? bymonthday.toString() : ""}
              onChange={(event: any) => setBymonthday(stringToArray(event.target.value))}
              disabled={bymonth.length === 0}
            />
          </Grid>
        );
      case RRule.MONTHLY:
        return (
          <Grid item className={classNames("rules", "monthly")}>
            {/* On specific days of a monthly interval */}
            <TextField
              label="every"
              className="monthly_interval_selection"
              name="interval"
              value={interval}
              onChange={(event: any) => setInterval(event.target.value)}
              inputProps={{ min: 1, max: 99 }}
              type="number"
              // disabled={bymonth.length === 0}
            />
            <TextField
              label={`${interval > 1 ? "months" : "month"}, on the`}
              className="dayofmonth_selection"
              name="interval"
              value={bymonthday ? bymonthday.toString() : ""}
              onChange={(event: any) => setBymonthday(stringToArray(event.target.value))}
            />
          </Grid>
        );
      case RRule.WEEKLY:
        return (
          <Grid item className={classNames("rules", "weekly")}>
            {/* On specific days of the week of a weekly interval */}
            <TextField
              label="every"
              className="weekly_interval_selection"
              name="interval"
              value={interval}
              onChange={(event: any) => setInterval(event.target.value)}
              inputProps={{ min: 1, max: 99 }}
              type="number"
            />
            <SelectField
              label={`${interval > 1 ? "weeks" : "week"}, on `}
              className="weekday_selection freqSelect"
              name="rrule_byweekday"
              options={weekdayOptions}
              onChange={(value: any) => setByweekday(value)}
              value={byweekday}
              isMulti={true}
              isOptionSelected={(option: any, selection: any) => selection.includes(option)}
              hideSelectedOptions={false}
              openMenuOnFocus={false}
              isClearable={false}
              {...selectionFieldProps}
            />
          </Grid>
        );
      case RRule.DAILY:
        return (
          <Grid item className={classNames("rules", "daily")}>
            {/* On a daily interval */}
            <TextField
              label="every"
              className="daily_interval_selection"
              name="interval"
              value={interval}
              onChange={(event: any) => setInterval(event.target.value)}
              inputProps={{ min: 1, max: 99 }}
              type="number"
            />
          </Grid>
        );
      default:
        return null;
    }
  };

  const hasError = meta?.error ? true : false;

  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error"
      )}
      sx={(theme) => RRuleStyles(theme).root}
      error={hasError}
    >
      <div className="input_control">
        <InputLabel htmlFor={"id-" + name + "-date"}>{label}</InputLabel>
        {/* Recurrence Freq Selection */}
        <Grid container direction="column">
          {renderRule()}
          <Grid container direction="row" className={classNames("rrule_generator", editmode && "show")}>
            <Grid item style={{ width: "auto" }}>
              <SelectField
                className="frequency_selection"
                name="rrule_freqOptions"
                isMulti={false}
                options={freqOptions}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={(value: any) => setFreq(value)}
                value={freq}
                menuPosition="fixed"
                placeholder="Choose Frequency..."
                {...selectionFieldProps}
              />
            </Grid>
            {renderFrequency()}
            {onReset && (
              <Button
                style={{ fontSize: 12, padding: "2px 8px", margin: 2 }}
                onClick={() => onReset()}
                variant="contained"
                color="secondary"
                disabled={!dirty}
              >
                Reset
              </Button>
            )}
            <Button
              style={{ fontSize: 12, padding: "2px 8px", margin: 2 }}
              variant="contained"
              disabled={!enableSave}
              onClick={() => handleSaveRRuleChanges()}
            >
              {saveButtonLabel ?? "Save"}
            </Button>
          </Grid>
        </Grid>
      </div>
      <FieldError meta={meta} />
    </FormControl>
  );
};
